import MetaRefreshRedirect from 'components/MetaRefreshRedirect';
import { OnboardingProvider } from 'hooks/useOnboarding';
import { isProduction } from 'lib/environment';
import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { getSingleTrackRoutePaths } from 'services/libraryItem/singleTrackRouting';
import { BaseLocales } from '../locales/i18nLingui';
import { homePageEnglishVariantLang } from './constants';
import DevelopmentRoute from './DevelopmentRoute';
import HeaderLayout from './HeaderLayout';

const FunnelHome = lazy(() => import('views_2/FunnelHome'));
const MediaEnquiries = lazy(() => import('Views/MediaEnquiries'));
const Local = lazy(() => import('Views/LocalPage'));
const MeditationOrigins = lazy(() => import('Views/Topics/med_origins'));
const MeditationBenefits = lazy(() => import('Views/Topics/med_benefits'));
const MeditationPractices = lazy(() => import('Views/Topics/med_practices'));

const PasswordReset = lazy(() => import('components/PasswordReset'));
const PageNotFound = lazy(() => import('components_2/pageNotFound'));

const FreeTimer = lazy(() => import('views_2/FreeTimer'));
const SeeAllLocalTeachers = lazy(() => import('views_2/LocalTeachers'));
const SeeAllLocalTeachersDynamic = lazy(
  () => import('views_2/LocalTeachers/LocalTeachersDynamic')
);

const Auth = lazy(() => import('views_2/Auth'));
const ResetPassword = lazy(() => import('views_2/ResetPassword'));
const UserProfile = lazy(() => import('views_2/User/profile'));
const ManageSubscription = lazy(() => import('views_2/ManageSubscription'));
const Invoices = lazy(() => import('views_2/ManageSubscription/Invoices'));
const Player = lazy(() => import('views_2/Player'));
const PlaylistBrowse = lazy(() => import('screens/Playlist/browse'));
const PlaylistDetails = lazy(() => import('screens/Playlist/details'));
const Hashtag = lazy(() => import('views_2/Hashtag'));
const PlaylistHashTag = lazy(() => import('views_2/Playlists/hashtag'));
const MyPlaylists = lazy(() => import('views_2/MyPlaylists'));
const LibraryItemDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "library-item-details" */ 'screens/LibraryItemPage/details'
    )
);
const RedirectLibraryItemDetails = lazy(
  () => import('screens/LibraryItemPage/details/RedirectLibraryItemDetails')
);
const BrowsePage = lazy(() => import('screens/LibraryItemPage/browse'));
const BrowsePageByTopic = lazy(
  () => import('screens/LibraryItemPage/browse/topic')
);
const Teachers = lazy(() => import('views_2/Publisher/browse'));
const MeditationTeachersJoin = lazy(() => import('screens/Teacher/join'));
const Live = lazy(() => import('views_2/Live'));
const MyBookmarks = lazy(() => import('views_2/MyBookmarks'));
const TopicsAll = lazy(() => import('views_2/Topics/seeAll'));
const TopicKidsDetails = lazy(() => import('views_2/Topics/details/kids'));
const MemberPlusV2 = lazy(() => import('views_2/MemberPlus/v2'));
const LocalPublisher = lazy(() => import('views_2/Local'));
const LocalPublisherDynamic = lazy(() => import('views_2/Local/LocalDynamic'));

const LocalDirectory = lazy(() => import('directory/local'));
const CourseDirectory = lazy(() => import('directory/course'));
const TeacherDirectory = lazy(() => import('directory/teachers'));
const PlaylistsDirectory = lazy(() => import('directory/playlist'));
const GmDirectory = lazy(() => import('directory/gms'));
const GmByTopicDirectory = lazy(() => import('directory/gms/details'));
const RetreatsDirectory = lazy(() => import('directory/retreats'));

const HelpNow = lazy(() => import('views_2/HelpNow'));
const Yoga = lazy(() => import('views_2/Yoga'));
const SearchResultsPage = lazy(() => import('views_2/Search'));
const Circle = lazy(() => import('views_2/Circles/Circle'));

const FreeTrial = lazy(() => import('components/FreeTrial'));
const CourseBrowsePage = lazy(() => import('screens/Courses/browse'));
const CoursesDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "courses-details" */ 'Views/Courses/CoursesDetailsPages'
    )
);

const WorkShopDetailsPage = lazy(
  () => import('views_2/Workshop/WorkshopDetailsPage')
);

const WorkshopCheckoutFlow = lazy(
  () => import('views_2/Workshop/WorkshopCheckoutFlow')
);

const ShopProductRedirectionPage = lazy(
  () => import('views_2/Workshop/ShopProductRedirectionPage')
);

const PurchaseConfirmationPage = lazy(
  () =>
    import('views_2/Workshop/PurchaseConfirmationPage/PurchaseConfirmationPage')
);

const ShopProductCheckoutMiddlewarePage = lazy(
  () =>
    import(
      'views_2/Workshop/PurchaseConfirmationPage/ShopProductCheckoutMiddlewarePage'
    )
);

const B2BWorkshopFrontPage = lazy(
  () => import('views_2/Workshop/B2BWorkshop/B2BWorkshopFrontPage')
);

const MemberPlusThirtyDaysTrialPage = lazy(
  () => import('views_2/MemberPlusTrial/MemberPlusThirtyDaysTrialPage')
);

const MemberPlusSevenDaysTrialPage = lazy(
  () => import('views_2/MemberPlusTrial/MemberPlusSevenDaysTrialPage')
);

// Use by App
const MemberPlusAppSubscriptionPage = lazy(
  () => import('views_2/AppSubscription/MemberPlusAppSubscriptionPage')
);

const MeditationGroupsPage = lazy(() => import('views_2/MeditationGroupsPage'));

const TeacherProfilePage = lazy(() => import('screens/PublisherProfile'));

const RedirectPage = lazy(() => import('views_2/Redirect'));

const WalmartCodeRedeemFlow = lazy(
  () => import('views_2/WalmartCodeRedeemFlow')
);

const WalmartLandingPage = lazy(
  () => import('views_2/WalmartCodeRedeemFlow/WalmartLandingPage')
);

const WalmartLandingVariantPage = lazy(
  () =>
    import(
      'views_2/WalmartCodeRedeemFlow/WalmartLandingPage/WalmartLandingVariantPage'
    )
);

const RedeemPage = lazy(() => import('views_2/Redeem'));

const MemberPlusLandingPage = lazy(
  () => import('views_2/MemberPlusLandingPage')
);

const EditorialPage = lazy(() => import('views_2/Editorial'));

const EditorialFeaturedTest = lazy(
  () => import('views_2/Editorial/EditorialFeaturedTest')
);
const EditorialDetailPage = lazy(
  () => import('views_2/Editorial/EditorialDetailPage')
);

const EditorialSEOPage = lazy(() => import('views_2/EditorialSEOPage'));

const VerifySessionPage = lazy(() => import('views_2/Auth/VerifySession'));

const Legal = lazy(() => import('views_2/Legal'));

const BranchDownloadAppPage = lazy(
  () => import('views_2/BranchDownloadAppPage')
);

const MeditationMusicPages = lazy(() => import('./MeditationMusicPages'));
const MeditationTopicPages = lazy(() => import('./MeditationTopicPages'));

const GlobalComponents = lazy(
  () => import('global_components/GlobalComponents')
);

const LgTvSubscriptionPage = lazy(
  () => import('views_2/LgTvSubscription/LgTvSubscriptionPage')
);

const PodcastTracksPage = lazy(
  () => import('views_2/TagBasedTracks/PodcastTracksPage')
);
const PublishingPage = lazy(() => import('views_2/PublishingPage'));

const QuoteDownloadAppPage = lazy(() => import('views_2/QuoteDownloadAppPage'));

const CMSPreview = lazy(() => import('views_2/CMSPreview'));

const I18nWrapper = lazy(() => import('hooks/useI18n'));

const RetreatsPages = lazy(() => import('Routes/RetreatsPages'));

const WellnessPage = lazy(() => import('views_2/Wellness'));

const Homepage = lazy(() => import('screens/Homepage'));

const AboutUs = lazy(() => import('screens/AboutUs'));

const ReviewPage = lazy(() => import('views_2/Review'));

const CareersPage = lazy(() => import('views_2/Careers'));

const BeginPage = lazy(() => import('screens/Begin'));

const PaymentSuccessPage = lazy(() => import('screens/Payment/SuccessPage'));

const PaymentCancelledPage = lazy(
  () => import('screens/Payment/CancelledPage')
);

const AISafetyPage = lazy(() => import('screens/AISafetyPage'));
const SleepPages = lazy(() => import('./SleepPages'));

const withI18nWrapper = <P extends object>(Component: React.ComponentType<P>) =>
  function WithI18nWrapper(props: P) {
    return (
      <Suspense fallback={<></>}>
        <I18nWrapper>
          <Component {...props} />
        </I18nWrapper>
      </Suspense>
    );
  };

const LocalWithI18n = withI18nWrapper(Local);
const MeditationOriginsWithI18n = withI18nWrapper(MeditationOrigins);
const MeditationBenefitsWithI18n = withI18nWrapper(MeditationBenefits);
const MeditationPracticesWithI18n = withI18nWrapper(MeditationPractices);
const LocalDirectoryWithI18n = withI18nWrapper(LocalDirectory);
const CourseDirectoryWithI18n = withI18nWrapper(CourseDirectory);
const TeacherDirectoryWithI18n = withI18nWrapper(TeacherDirectory);
const PlaylistsDirectoryWithI18n = withI18nWrapper(PlaylistsDirectory);
const GmDirectoryWithI18n = withI18nWrapper(GmDirectory);
const GmByTopicDirectoryWithI18n = withI18nWrapper(GmByTopicDirectory);
const TopicsAllWithI18n = withI18nWrapper(TopicsAll);
const TopicKidsDetailsWithI18n = withI18nWrapper(TopicKidsDetails);

const Pages = () => {
  const singleTrackRoutePaths = getSingleTrackRoutePaths();

  useEffect(() => {
    document.documentElement.lang = BaseLocales.English;
  }, []);

  return (
    <Suspense fallback={<></>}>
      <Switch>
        <Route
          path="/circles/:circleId"
          render={props => {
            return <Redirect to={`/groups/${props.match.params.circleId}`} />;
          }}
        />
        <Route path="/groups/:circleId" component={Circle} />

        {/* global-components */}
        <Route
          path="/global-components"
          component={DevelopmentRoute(GlobalComponents)}
        />

        {/* player */}
        <Route path="/play" component={Player} />

        {/* verify session and login using it */}
        <Route path="/verify-session" component={VerifySessionPage} />

        {/* free trial */}
        <Route exact path="/subscribe/free-trial" component={FreeTrial} />

        <Route path="/subscribe/lgtv" component={LgTvSubscriptionPage} />

        <Route
          path="/subscribe/walmartplus"
          component={DevelopmentRoute(WalmartCodeRedeemFlow)}
        />

        <Route path="/walmart" component={WalmartLandingPage} />

        <Route path="/trial" component={WalmartLandingVariantPage} />

        <Route path="/app/memberplus" component={MemberPlusLandingPage} />

        <Route exact path="/spotify" component={WalmartLandingVariantPage} />

        <Route
          exact
          path="/subscribe/30-day-trial"
          component={MemberPlusThirtyDaysTrialPage}
        />

        <Route
          exact
          path="/subscribe/7-day-trial"
          component={MemberPlusSevenDaysTrialPage}
        />

        <Route
          exact
          path="/subscribe"
          component={MemberPlusAppSubscriptionPage}
        />

        <Route exact path="/payment_success" component={PaymentSuccessPage} />

        <Route
          exact
          path="/payment_cancelled"
          component={PaymentCancelledPage}
        />

        <Route exact path="/legal/:slug" component={Legal} />

        <Route exact path="/redeem" component={RedeemPage} />

        <Route
          exact
          path="/download-app"
          component={DevelopmentRoute(BranchDownloadAppPage)}
        />

        <Route>
          <OnboardingProvider>
            <HeaderLayout>
              <Route
                exact
                path={`/:lang(${homePageEnglishVariantLang})`}
                component={Homepage}
              />

              <Route
                exact
                path={[
                  '/individuals',
                  `/:lang(${homePageEnglishVariantLang})/individuals`
                ]}
                component={() => <Redirect to="/" />}
              />

              <Route exact path="/begin" component={BeginPage} />

              <Route exact path="/wellness/:id" component={WellnessPage} />

              <Route
                exact
                path="/resources/therapists"
                component={() => <Redirect to="/therapists" />}
              />

              <Route exact path="/reviews" component={ReviewPage} />

              <Route exact path="/careers" component={CareersPage} />

              <Route
                exact
                path="/my-playlists"
                component={DevelopmentRoute(MyPlaylists)}
              />
              <Route
                exact
                path="/my-profile"
                component={DevelopmentRoute(UserProfile)}
              />
              <Route
                exact
                path="/my-bookmarks"
                component={DevelopmentRoute(MyBookmarks)}
              />

              <Route
                exact
                path="/preview/:slug"
                component={DevelopmentRoute(CMSPreview)}
              />

              {/* live */}
              <Route exact path="/live" component={Live} />

              <Route
                exact
                path="/live/:eventId"
                render={props => {
                  window.location.replace(
                    `${process.env.REACT_APP_WEBAPP_HOST}${props.location.pathname}`
                  );
                  return null;
                }}
              />

              <Route
                exact
                path="/users/:userId"
                render={props => {
                  window.location.replace(
                    `${process.env.REACT_APP_WEBAPP_HOST}${props.location.pathname}`
                  );
                  return null;
                }}
              />

              <Route exact path="/yoga" component={Yoga} />
              <Route exact path="/search" component={SearchResultsPage} />
              <Route
                exact
                path="/yoga/:slug"
                render={props => (
                  <I18nWrapper>
                    <EditorialSEOPage
                      slug={`yoga--${props.match.params.slug}`}
                    />
                  </I18nWrapper>
                )}
              />

              <Route
                exact
                path="/editorial"
                component={!isProduction ? EditorialPage : PageNotFound}
              />

              <Route
                exact
                path="/editorial/featured-test"
                component={!isProduction ? EditorialFeaturedTest : PageNotFound}
              />

              <Route
                exact
                path="/editorial/:slug"
                component={!isProduction ? EditorialDetailPage : PageNotFound}
              />

              <Route exact path="/ai-safety" component={AISafetyPage} />

              <Route
                path="/:publisherSlug/workshops/:productId"
                component={WorkShopDetailsPage}
                exact
              />

              <Route
                exact
                path="/:publisherSlug/workshops/:productId/confirmation"
                component={PurchaseConfirmationPage}
              />

              <Route
                exact
                path="/marketplace"
                component={B2BWorkshopFrontPage}
              />

              <Route
                path="/shop_products/:productId"
                component={ShopProductRedirectionPage}
                exact
              />

              <Route
                path="/shop_products/:productId/cancel"
                component={ShopProductRedirectionPage}
                exact
              />

              <Route
                path="/shop_products/:productId/success"
                component={ShopProductCheckoutMiddlewarePage}
                exact
              />

              <Route
                path="/:publisherSlug/workshops/:productId/:tab"
                component={WorkshopCheckoutFlow}
              />

              <Route exact path="/" component={Homepage} />

              <Route
                exact
                path={[
                  '/:lang(br)/meditação-guiada/:page([1-9]d*)?',
                  '/:lang(br)/meditação-guiada/:filter(popular)'
                ]}
                component={BrowsePage}
              />

              <Route
                exact
                path="/dir/local"
                component={LocalDirectoryWithI18n}
              />

              <Route
                sensitive
                exact
                path="/local/other/:decodeRegionData"
                component={LocalPublisherDynamic}
              />

              <Route
                sensitive
                exact
                path="/local/other/:decodeRegionData/meditation-teachers/:page([1-9]\d*)?"
                component={SeeAllLocalTeachersDynamic}
              />

              <Route
                sensitive
                exact
                path="/local/:country/:city"
                component={LocalPublisher}
              />

              <Route
                sensitive
                exact
                path="/local/:country/:city/meditation-teachers/:page([1-9]\d*)?"
                component={SeeAllLocalTeachers}
              />
              <Route exact path="/local" component={LocalWithI18n} />

              <Route
                exact
                path="/meditation-playlists"
                component={PlaylistBrowse}
              />

              <Route exact path="/hashtag" component={Hashtag} />

              <Route
                exact
                path="/hashtag/:hashtag"
                component={PlaylistHashTag}
              />

              <Route
                exact
                path="/meditation-playlists/:id"
                component={({ match }: RouteComponentProps<{ id: string }>) => (
                  <Redirect to={`/playlists/${match.params.id}`} />
                )}
              />
              <Route exact path="/playlists/:id" component={PlaylistDetails} />

              <Route exact path="/publish" component={PublishingPage} />
              {/* Course  directory */}
              <Route
                exact
                path="/dir/meditation-courses"
                component={CourseDirectoryWithI18n}
              />

              {/* Teacher  directory */}
              <Route
                exact
                path="/dir/meditation-teachers"
                component={TeacherDirectoryWithI18n}
              />
              <Route
                sensitive
                path="/dir/meditation-teachers/:startwith(hash|more|[a-z]{1})"
                component={TeacherDirectoryWithI18n}
              />

              {/* Playlists  directory */}
              <Route
                exact
                path="/dir/playlists"
                component={PlaylistsDirectoryWithI18n}
              />
              <Route
                sensitive
                path="/dir/playlists/:startwith(hash|more|[a-z]{1})"
                component={PlaylistsDirectoryWithI18n}
              />

              {/* Topic  directory */}
              <Route
                exact
                path="/dir/guided-meditations"
                component={GmDirectoryWithI18n}
              />
              <Route
                exact
                path="/dir/guided-meditations/:meditationTopic"
                component={GmByTopicDirectoryWithI18n}
              />
              <Route
                sensitive
                exact
                path="/dir/guided-meditations/:meditationTopic/:startwith(hash|more|[a-z]{1})"
                component={GmByTopicDirectoryWithI18n}
              />

              <Route
                exact
                path="/dir/retreats/:location?"
                component={RetreatsDirectory}
              />

              {/* Login */}
              <Route
                exact
                path="/login"
                render={props => <Auth authType="login" {...props} />}
              />
              <Route
                exact
                path="/signup"
                render={props => <Auth authType="signup" {...props} />}
              />
              <Route
                exact
                path="/logout"
                render={props => <Auth authType="logout" {...props} />}
              />

              <Route exact path="/reset-password" component={ResetPassword} />

              <Route exact path="/password-reset" component={PasswordReset} />

              {/* User */}

              {/* Manage subscription */}
              <Route
                exact
                path="/manage-subscription"
                component={ManageSubscription}
              />
              <Route
                exact
                path="/manage-subscription/invoices"
                component={Invoices}
              />

              {/* static */}
              <Route exact path="/meditation-app" component={FunnelHome} />
              <Route exact path="/about" component={AboutUs} />

              <Route exact path="/member-plus" component={MemberPlusV2} />
              <Route
                exact
                path="/premium"
                component={() => <Redirect to="/member-plus" />}
              />
              <Route
                exact
                path={['/meditation-timer', '/meditation-timer/:duration']}
                component={FreeTimer}
              />
              <Route exact path="/media-enquiries" component={MediaEnquiries} />
              {/* <Route exact={true} path="/sitemap" component={Sitemap} /> */}

              <Route
                exact
                path="/meditation-groups"
                component={MeditationGroupsPage}
              />
              <Route
                exact
                path="/meditation-groups/:slug"
                component={() => <Circle />}
              />
              <Route
                exact
                path="/meditation-groups/:slug/join"
                component={() => <Circle />}
              />

              <Route
                exact
                path="/guided-meditations/:itemId"
                component={RedirectLibraryItemDetails}
              />

              <Route
                exact
                path="/guided-meditations/:itemId/:lang"
                component={RedirectLibraryItemDetails}
              />

              {/* guided meditations */}
              <Route
                exact
                path={[
                  '/guided-meditations/:page([1-9]d*)?',
                  '/guided-meditations/:filter(popular)'
                ]}
                component={BrowsePage}
              />

              <Route exact path="/podcasts" component={PodcastTracksPage} />

              <Route
                exact
                path={singleTrackRoutePaths.all.map(
                  segment => `/:teacher/${segment}/:slug`
                )}
                component={LibraryItemDetails}
              />

              {/* <Route exact={true} path="/guided-meditations/:filter(new|popular|staff-picks|browse)?" component={BrowsePage} /> */}

              {/* teachers */}
              <Route exact path="/meditation-teachers" component={Teachers} />

              <Route
                path="/meditation-teachers/join"
                component={MeditationTeachersJoin}
              />

              <Route
                exact
                path="/meditation-teachers/:slug(popular|new|starts-with-[aA-zZ]{1})/:page([1-9]\d*)?"
                component={Teachers}
              />

              {/* courses */}
              <Route
                exact
                path="/meditation-courses"
                component={CourseBrowsePage}
              />

              <Route
                exact
                path="/meditation-courses/:slug"
                component={CoursesDetailsPage}
              />

              <Route
                exact
                path="/meditation-topics/coronavirus"
                component={() => (
                  <MetaRefreshRedirect to="/meditation-topics" />
                )}
              />

              <Route
                exact
                path="/meditation-topics/music"
                component={() => <Redirect to="/meditation-music" />}
              />

              <Route
                exact
                path="/meditation-topics/:topic/browse/all"
                component={({
                  match
                }: RouteComponentProps<{ topic: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.topic}`} />
                )}
              />
              <Route
                exact
                path="/meditation-topics/:topic/browse/:type(talks|music|guided)/:page([1-9]\d*)?"
                component={({
                  match
                }: RouteComponentProps<{
                  topic: string;
                  type: string;
                  page?: string;
                }>) => {
                  const pagePath = match.params.page
                    ? `/${match.params.page}`
                    : '';
                  if (match.params.type === 'music') {
                    return (
                      <Redirect
                        to={`/meditation-music/browse/${match.params.topic}${pagePath}`}
                      />
                    );
                  }

                  return <BrowsePageByTopic type="talks" />;
                }}
              />
              <Route
                path="/meditation-topics/:topic/browse/:type/:page([^-]*)"
                component={({
                  match
                }: RouteComponentProps<{ topic: string }>) => (
                  <Redirect
                    to={`/meditation-topics/${match.params.topic}/browse/guided`}
                  />
                )}
              />
              <Route
                exact
                path="/meditation-topics/:topic/browse"
                component={({
                  match
                }: RouteComponentProps<{ topic: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.topic}`} />
                )}
              />

              <Route
                exact
                path="/meditation-practices"
                component={MeditationPracticesWithI18n}
              />
              <Route
                exact
                path="/meditation-origins"
                component={MeditationOriginsWithI18n}
              />
              <Route
                exact
                path="/meditation-benefits"
                component={MeditationBenefitsWithI18n}
              />

              <Route
                path="/meditation-origins/:slug"
                component={({
                  match
                }: RouteComponentProps<{ slug: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.slug}`} />
                )}
              />
              <Route
                path="/meditation-benefits/:slug"
                component={({
                  match
                }: RouteComponentProps<{ slug: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.slug}`} />
                )}
              />
              <Route
                path="/meditation-practices/:slug"
                component={({
                  match
                }: RouteComponentProps<{ slug: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.slug}`} />
                )}
              />

              <Route
                exact
                path="/meditation-topics"
                component={TopicsAllWithI18n}
              />
              <Route
                path="/meditation-music"
                component={MeditationMusicPages}
              />
              <Route
                exact
                path="/meditation-topics/children"
                render={props => {
                  // eslint-disable-next-line no-param-reassign
                  props.match.params.slug = 'children';
                  return <TopicKidsDetailsWithI18n {...props} />;
                }}
              />

              <Route
                exact
                path="/meditation-topics/sleep"
                render={() => <Redirect to={`/sleep/sleep-meditations`} />}
              />

              <Route
                exact
                path="/meditation-topics/sleep_meditation"
                render={() => <Redirect to={`/sleep/sleep-meditations`} />}
              />

              <Route
                exact
                path="/meditation-topics/sleep_meditations"
                render={() => <Redirect to={`/sleep/sleep-meditations`} />}
              />

              <Route
                exact
                path="/meditation-topics/bedtime"
                render={() => <Redirect to={`/sleep/bedtime-stories`} />}
              />

              <Route
                exact
                path="/meditation-topics/sleep/bedtime-stories"
                render={() => (
                  <Redirect
                    to={`/sleep/bedtime-stories/bedtime-stories-guide`}
                  />
                )}
              />

              <Route
                exact
                path="/meditation-topics/sleep/guide-healthy-sleep-habits"
                render={() => (
                  <Redirect
                    to={`/sleep/sleep-meditations/better-sleep-guide`}
                  />
                )}
              />

              <Route
                exact
                path="/meditation-topics/sleep/sleep-quality"
                render={() => (
                  <Redirect
                    to={`/sleep/sleep-meditations/sleep-quality-guide`}
                  />
                )}
              />

              <Route
                exact
                path="/meditation-topics/sleep/sleep-faster"
                render={() => (
                  <Redirect
                    to={`/sleep/sleep-meditations/fall-asleep-faster-guide`}
                  />
                )}
              />

              <Route
                exact
                path="/meditation-topics/sleep_music"
                render={() => <Redirect to={`/sleep/sleep-music`} />}
              />

              <Route
                exact
                path="/meditation-music/browse/sleep_music"
                render={() => <Redirect to={`/sleep/sleep-music`} />}
              />

              <Route
                exact
                path="/meditation-topics/ambientmusic"
                render={() => (
                  <Redirect to={`/sleep/sleep-music/ambient-music`} />
                )}
              />

              <Route
                exact
                path="/meditation-topics/sleep_sounds"
                render={() => <Redirect to={`/sleep/sleep-sounds`} />}
              />

              <Route
                path={[
                  '/meditation-topics/:slug/tabs',
                  '/topics/:slug/tabs',
                  '/topics/:slug'
                ]}
                component={({
                  match
                }: RouteComponentProps<{ slug: string }>) => (
                  <Redirect to={`/meditation-topics/${match.params.slug}`} />
                )}
              />
              <Route
                exact
                path={['/meditation-topics/:slug']}
                component={MeditationTopicPages}
              />

              {/* topics */}
              <Route
                exact
                path="/meditation-topics/:topic/:subtopic"
                render={props => {
                  const { topic, subtopic } = props.match.params;

                  return (
                    <I18nWrapper>
                      <EditorialSEOPage
                        slug={`meditation-topics-${topic}--${subtopic}`}
                      />
                    </I18nWrapper>
                  );
                }}
              />

              <Route
                exact
                path="/explore/:slug"
                render={props => {
                  const { slug } = props.match.params;
                  return <Redirect to={`/meditation-topics/${slug}`} />;
                }}
              />

              {/* local */}
              <Route
                exact
                path="/:citySlug(newyork-us|losangeles-us|london-gb|sanfrancisco-us|chicago-us|washington-us|sydney-au|melbourne-au|seattle-us|toronto-ca)/meditation"
                render={props => {
                  const locArray = props.match.params.citySlug.split('-');
                  const country = locArray[1];
                  let newCity = locArray[0];

                  switch (newCity) {
                    case 'newyork':
                      newCity = 'new-york';
                      break;
                    case 'losangeles':
                      newCity = 'los-angeles';
                      break;
                    case 'sanfrancisco':
                      newCity = 'san-francisco';
                      break;
                    default:
                      break;
                  }

                  return <Redirect to={`/local/${country}/${newCity}`} />;
                }}
              />

              <Route exact path="/quote" component={QuoteDownloadAppPage} />

              <Route exact path="/help-now" component={HelpNow} />

              <Route exact path="/redirect" component={RedirectPage} />

              <Route
                path={['/retreats', '/retreat']}
                component={RetreatsPages}
              />

              <Route path="/sleep" component={SleepPages} />

              {/* TeacherProfile */}
              <Route
                exact
                path="/:teacherSlug/home"
                render={props => {
                  return (
                    <MetaRefreshRedirect
                      to={`/${props.match.params.teacherSlug}`}
                    />
                  );
                }}
              />
              <Route exact path="/:slug/:tab?" component={TeacherProfilePage} />

              <Route path="*" component={PageNotFound} />
            </HeaderLayout>
          </OnboardingProvider>
        </Route>
      </Switch>
    </Suspense>
  );
};

export default Pages;
